/**
 * Teacher Settings Utility
 *
 * Manages teacher-specific preferences stored in localStorage.
 * Settings are per-device and persist across sessions.
 */

const STORAGE_KEY = 'teacher_enterKeyShortcut_enabled';

/**
 * Gets the teacher's Enter key shortcut preference.
 *
 * @returns {boolean} true if Enter key shortcut is enabled, false otherwise
 *
 * When localStorage is unavailable or the key doesn't exist,
 * defaults to true (shortcut enabled).
 */
export function getEnterKeyShortcutPreference(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    // If no value is stored, default to true (enabled)
    if (value === null) {
      return true;
    }
    return value === 'true';
  } catch (error) {
    // localStorage unavailable (private mode, etc.)
    console.warn('localStorage unavailable, defaulting Enter key shortcut to enabled:', error);
    return true;
  }
}

/**
 * Sets the teacher's Enter key shortcut preference.
 *
 * @param {boolean} value - true to enable Enter key shortcut, false to disable
 *
 * If localStorage is unavailable, the setting is not persisted
 * but the function fails gracefully.
 */
export function setEnterKeyShortcutPreference(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch (error) {
    console.warn('Could not save Enter key shortcut preference to localStorage:', error);
    // Fail gracefully - the feature will just reset to default on refresh
  }
}
